// GTM / dataLayer helpers for GA4 ecommerce events.
// All events merge the session's stored inbound tracking params so that
// campaign attribution is carried through the entire funnel.

import type { InboundParams } from '@/lib/tracking/inbound-params';
import { getInboundParams } from '@/lib/tracking/inbound-params';
import type { ShoppingCartItem, MiniShoppingCartItem } from '@/lib/types/cart';
import type { OrderItem } from '@/lib/types/order';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

// ---------- Core helper ----------

export function pushDataLayer(event: string, data: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // Clear any stale ecommerce object before pushing a new event so that GTM
  // tags cannot accidentally inherit fields from a previous ecommerce push.
  if ('ecommerce' in data) {
    window.dataLayer.push({ ecommerce: null });
  }
  window.dataLayer.push({ event, ...data });
}

// ---------- Attribution params ----------

function inboundParamsPayload(): Record<string, unknown> {
  const p: InboundParams | null = getInboundParams();
  if (!p) return {};
  return {
    utm_source: p.utm_source ?? undefined,
    utm_medium: p.utm_medium ?? undefined,
    utm_campaign: p.utm_campaign ?? undefined,
    utm_term: p.utm_term ?? undefined,
    utm_content: p.utm_content ?? undefined,
    campaign_id: p.campaign_id ?? undefined,
    original_url: p.original_url,
  };
}

// ---------- GA4 item mappers ----------

export function cartItemToGa4(item: ShoppingCartItem) {
  return {
    item_id: String(item.productId),
    item_name: item.productName ?? undefined,
    item_variant: item.sku ?? undefined,
    price: item.unitPriceValue,
    quantity: item.quantity,
  };
}

export function miniCartItemToGa4(item: MiniShoppingCartItem, quantity: number) {
  return {
    item_id: String(item.productId),
    item_name: item.productName ?? undefined,
    price: item.unitPriceValue,
    quantity,
  };
}

export function orderItemToGa4(item: OrderItem) {
  return {
    item_id: String(item.ProductId),
    item_name: item.ProductName,
    item_variant: item.Sku ?? undefined,
    price: item.UnitPriceInclTaxValue,
    quantity: item.Quantity,
  };
}

// ---------- Attribution captured (once per session) ----------

export function pushAttributionCaptured(params: InboundParams): void {
  pushDataLayer('attribution_captured', {
    utm_source: params.utm_source ?? undefined,
    utm_medium: params.utm_medium ?? undefined,
    utm_campaign: params.utm_campaign ?? undefined,
    utm_term: params.utm_term ?? undefined,
    utm_content: params.utm_content ?? undefined,
    campaign_id: params.campaign_id ?? undefined,
    original_url: params.original_url,
    captured_at: params.captured_at,
  });
}

// ---------- GA4 ecommerce events ----------

export function pushAddToCart(item: MiniShoppingCartItem, quantity: number): void {
  pushDataLayer('add_to_cart', {
    ecommerce: {
      currency: 'USD',
      value: item.unitPriceValue * quantity,
      items: [miniCartItemToGa4(item, quantity)],
    },
    ...inboundParamsPayload(),
  });
}

export function pushViewCart(items: ShoppingCartItem[], value: number): void {
  pushDataLayer('view_cart', {
    ecommerce: {
      currency: 'USD',
      value,
      items: items.map(cartItemToGa4),
    },
    ...inboundParamsPayload(),
  });
}

export function pushBeginCheckout(items: ShoppingCartItem[], value: number): void {
  pushDataLayer('begin_checkout', {
    ecommerce: {
      currency: 'USD',
      value,
      items: items.map(cartItemToGa4),
    },
    ...inboundParamsPayload(),
  });
}

export function pushPurchase(
  transactionId: string,
  value: number,
  items: OrderItem[],
): void {
  pushDataLayer('purchase', {
    ecommerce: {
      transaction_id: transactionId,
      currency: 'USD',
      value,
      items: items.map(orderItemToGa4),
    },
    ...inboundParamsPayload(),
  });
}
